// Frontend encryption utility for AWS credentials
import CryptoJS from 'crypto-js';

/**
 * Encrypts data using AES-256-GCM
 * @param {Object} data - Data to encrypt
 * @param {string} encryptionKey - Encryption key
 * @returns {Object} - Encrypted data with IV
 */
export function encryptData(data, encryptionKey) {
  try {
    const dataString = JSON.stringify(data);

    // Generate a random IV (Initialization Vector)
    const iv = CryptoJS.lib.WordArray.random(16);

    // Encrypt the data
    const encrypted = CryptoJS.AES.encrypt(dataString, encryptionKey, {
      iv: iv,
      mode: CryptoJS.mode.GCM,
      padding: CryptoJS.pad.Pkcs7,
    });

    return {
      encryptedData: encrypted.toString(),
      iv: iv.toString(),
      algorithm: 'AES-256-GCM',
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    throw new Error('Failed to encrypt data');
  }
}

/**
 * Encrypts AWS credentials specifically
 * @param {Object} awsCredentials - AWS credentials object
 * @param {string} encryptionKey - Encryption key
 * @returns {Object} - Encrypted credentials
 */
export function encryptAWSCredentials(awsCredentials, encryptionKey) {
  if (!awsCredentials || !encryptionKey) {
    throw new Error('AWS credentials and encryption key are required');
  }

  // Only encrypt sensitive data
  const sensitiveData = {
    accessKeyId: awsCredentials.accessKeyId,
    secretAccessKey: awsCredentials.secretAccessKey,
    sessionToken: awsCredentials.sessionToken,
  };

  const encrypted = encryptData(sensitiveData, encryptionKey);

  // Return non-sensitive data in plain text along with encrypted sensitive data
  return {
    region: awsCredentials.region,
    bucketName: awsCredentials.bucketName,
    encryptedCredentials: encrypted,
  };
}

/**
 * Validates encryption configuration
 * @param {Object} encryptionConfig - Encryption configuration
 * @returns {boolean} - Whether configuration is valid
 */
export function validateEncryptionConfig(encryptionConfig) {
  if (!encryptionConfig || !encryptionConfig.enabled) {
    return false;
  }

  if (!encryptionConfig.encryptionKey || encryptionConfig.encryptionKey.length < 32) {
    console.warn('Encryption key should be at least 32 characters long');
    return false;
  }

  return true;
}
